import { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, makeStyles } from '@material-ui/core';
import TextBoxWithTooltip from '../textBoxWithTooltip';
import StatusIndicator from '../statusIndicator';
import ProposedStatus from '../proposedStatus';
import RequestedIconTooltip from '../requestedIconTooltip';
import TasksIconTooltip from '../tasksIconTooltip';
import CoursesIconTooltip from '../coursesIconTooltip';
import Tag from '../tag';
import AsyncListBadge from '../asyncListBadge';
import UserLink from '../userLink';
import { ReactComponent as TagsIcon } from '../../../assets/icons/tag-dark.svg';
import { ReactComponent as CalendarIcon } from '../../../assets/icons/event_note.svg';
import { ReactComponent as CalendarIconAccepted } from '../../../assets/icons/event_available_small_dark.svg';
import { formatDate } from '../../../utility/dateUtils';
import { isArrayEmpty } from '../../../utility/helpers';
import { DUE_DATE_TIME_FORMAT } from '../../../constants/actionPlan';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    backgroundColor: primary.white,
    boxSizing: 'border-box',
    borderRadius: 16,
    cursor: 'default',
    display: 'flex',
    flexDirection: 'column',
    padding: spacing(4),
    width: 270,
    minHeight: 170,
    userSelect: 'none',
  },
  cardAccessible: {
    cursor: 'pointer',
  },
  title: {
    marginBottom: spacing(2),
  },
  dateWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 'auto',
  },
  dateWrapperNoSpacing: {
    marginTop: 0,
  },
  dateLabel: {
    color: primary.bluish4,
    lineHeight: '18px',
    marginBottom: spacing(1),
  },
  statusWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 24,
    marginBottom: spacing(2),
  },
  status: {
    display: 'flex',
    alignItems: 'center',
  },
  progressWrapper: {
    boxSizing: 'border-box',
    backgroundColor: primary.black,
    padding: spacing(1, 2),
    height: 20,
  },
  progressValue: {
    textTransform: 'uppercase',
    userSelect: 'none',
    marginLeft: spacing(2),
  },
  infoWrapper: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(0,max-content))',
    gridColumnGap: 8,
    alignItems: 'center',
    marginBottom: spacing(1.5),
  },
  badge: {
    flexFlow: 'row-reverse',
    padding: spacing(0, 1.5),
    height: 22,
  },
  badgeIcon: {
    marginLeft: 0,
    marginRight: spacing(1),
    marginTop: spacing(-0.5),
  },
  tagCount: {
    fontSize: 14,
    lineHeight: '18px',
  },
  proposed: {
    marginLeft: spacing(1.5),
  },
  calendarIcon: {
    marginTop: spacing(-1),
    marginRight: spacing(2),
  },
  accessible: {
    cursor: 'pointer',
  },
  userWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 'auto',
    marginBottom: spacing(2),
    height: 24,
  },
  user: {
    maxWidth: 'calc(100% - 30px)',
  },
}));

const PlanMiniCard = ({
  className = undefined,
  translations,
  plan = {},
  onCardClick,
  canSeeCourses,
}) => {
  const classes = useStyles();
  const {
    is_proposed,
    title,
    due_date,
    created_on,
    user,
    status,
    progress,
    isCreatedForCurrentUser,
    isPendingProposal,
    tags,
    hasTasks,
    tasks,
    courses,
    hasCourses,
    canAccessPlanDetails,
    canAccessUserProfile,
  } = plan;
  const hasTags = !isArrayEmpty(tags);

  const handleCardClick = useCallback(() => {
    if (canAccessPlanDetails) {
      onCardClick(plan.id);
    }
  }, [plan, canAccessPlanDetails, onCardClick]);

  const getVirtualTagItemSize = useCallback(
    (index, items) => (index === items.length - 1 ? 20 : 28),
    []
  );

  const renderTag = tag => (
    <Tag tag={tag} color={tag?.category?.color} isSelected />
  );

  return (
    <div
      className={classNames(
        classes.root,
        { [classes.cardAccessible]: canAccessPlanDetails },
        className
      )}
      onClick={handleCardClick}
    >
      <div className={classes.statusWrapper}>
        <div className={classes.status}>
          {isPendingProposal ? (
            <ProposedStatus proposedLabel={translations.proposed} />
          ) : (
            <StatusIndicator status={status} />
          )}
          {!isPendingProposal && (
            <Typography className={classes.progressValue} variant="body2">
              {`${progress}%`}
            </Typography>
          )}
        </div>
        {isCreatedForCurrentUser && is_proposed && !isPendingProposal && (
          <RequestedIconTooltip
            className={classes.accessible}
            labels={translations.requested}
            isRequestedByCurrentUser={isCreatedForCurrentUser}
          />
        )}
      </div>
      <TextBoxWithTooltip
        className={classes.title}
        text={title}
        variant="subtitle1"
      />
      {(hasTasks || (canSeeCourses && hasCourses) || hasTags) && (
        <div className={classes.infoWrapper}>
          <TasksIconTooltip
            iconClass={classes.accessible}
            text={translations.tasksIconTooltip}
            numberOfTasks={tasks.length}
            isVisible={hasTasks}
          />
          <CoursesIconTooltip
            text={translations.coursesIconTooltip}
            isVisible={canSeeCourses && hasCourses}
            numberOfCourses={courses?.length}
          />
          {hasTags && (
            <AsyncListBadge
              badgeClass={classes.badge}
              badgeIconClass={classes.badgeIcon}
              countClass={classes.tagCount}
              tooltipTitle={translations.tags}
              count={tags?.length}
              items={tags}
              icon={TagsIcon}
              getItemSize={getVirtualTagItemSize}
              shouldDisablePortal={false}
              renderItem={renderTag}
            />
          )}
        </div>
      )}
      {!isCreatedForCurrentUser && (
        <div className={classes.userWrapper}>
          <UserLink
            className={classNames({ [classes.user]: is_proposed })}
            user={user}
            avatarProps={{
              variant: 'subtitle1',
            }}
            isUserAccessible={canAccessUserProfile}
          />
          {is_proposed && (
            <RequestedIconTooltip
              className={classNames(classes.proposed, classes.accessible)}
              labels={translations.requested}
            />
          )}
        </div>
      )}
      <div
        className={classNames(classes.dateWrapper, {
          [classes.dateWrapperNoSpacing]: !isCreatedForCurrentUser,
        })}
      >
        {isPendingProposal ? (
          <CalendarIcon className={classes.calendarIcon} />
        ) : (
          <CalendarIconAccepted className={classes.calendarIcon} />
        )}
        <Typography variant="body2">{`${formatDate(
          isPendingProposal ? created_on : due_date,
          DUE_DATE_TIME_FORMAT
        )}`}</Typography>
      </div>
    </div>
  );
};

PlanMiniCard.propTypes = {
  className: PropTypes.string,
  translations: PropTypes.object.isRequired,
  plan: PropTypes.object,
  onCardClick: PropTypes.func.isRequired,
  canSeeCourses: PropTypes.bool.isRequired,
};

export default PlanMiniCard;
